/**
 * LES VENTES DU GUICHET D'UNE AUTRE NUIT — LES PHRASES, SANS NAVIGATEUR.
 *
 * Une caisse Shotgun restée ouverte sur une ancienne soirée (03/10/2026 : 110
 * billets, 1 060 €, sur la soirée du 26/09) fausse l'Accueil de deux nuits.
 * Les écrans le DISENT en phrases ; ces phrases vivent ici, pures (ni React
 * ni réseau), pour que les tests les lisent telles que l'écran les écrit :
 *
 *   · la sous-ligne de la feuille de caisse (« dont 1 060 € vendus sur la
 *     soirée du 26/09, rattachés à cette nuit ») ;
 *   · la concordance reddition / billetterie, avec son SENS ;
 *   · l'effet d'un geste sur l'Accueil (« attendu 4 460 €, écart −1 € au lieu
 *     de +1 059 € ») — ce qui rend la décision évidente sans lire de notice.
 *
 * Un montant illisible (droit de la billetterie) n'est jamais écrit « 0 € » :
 * la phrase dit le nombre de billets, ou se tait.
 */

import { formatEcart, formatMoney } from './money.js'
import type { NightRedditionControlRow, OffNightSaleRow, StatutHorsNuit } from './nights-common.js'
import type { ChiffresPointDeVente } from './closing-rail-model.js'
import { decisionEnVigueur } from '../../../../../../packages/core/src/finance/feuille-de-caisse.js'

/** « 26/09 » : la nuit telle que la DAF l'écrit dans son suivi. */
export function jourMois(ymd: string): string { return `${ymd.slice(8, 10)}/${ymd.slice(5, 7)}` }

/** « 49 billets », « 1 billet ». */
const billets = (n: number): string => `${n} billet${n > 1 ? 's' : ''}`

/**
 * LA SOUS-LIGNE D'UN CAS DE VENTES HORS NUIT — une phrase, vue de la nuit
 * lue. Le montant quand on peut le lire, sinon le nombre de billets : jamais
 * un « 0 € » qui cacherait un droit manquant.
 */
export function phraseSousLigneHorsNuit(c: {
  sens: 'entrant' | 'sortant'; nuit: string; billets: number; cents: number | null; statut: StatutHorsNuit
}): string {
  const quoi = c.cents !== null ? formatMoney(c.cents) : `${c.billets} billet${c.billets > 1 ? 's' : ''}`
  const j = jourMois(c.nuit)
  if (c.statut === 'SANS_OBJET') return `ventes de la nuit du ${j} : plus hors de leur nuit, la décision ne s’applique plus`
  if (c.sens === 'entrant') {
    if (c.statut === 'RATTACHE') return `dont ${quoi} vendus sur la soirée du ${j}, rattachés à cette nuit`
    if (c.statut === 'MAINTENU') return `${quoi} vendus cette nuit sur la soirée du ${j}, maintenus sur leur soirée`
    return `${quoi} vendus cette nuit sur la soirée du ${j} : en attente de décision`
  }
  if (c.statut === 'RATTACHE') return `${quoi} de cette soirée vendus pendant la nuit du ${j}, rattachés à cette nuit-là`
  if (c.statut === 'MAINTENU') return `dont ${quoi} vendus pendant la nuit du ${j}, maintenus ici`
  return `${quoi} de cette soirée vendus pendant la nuit du ${j} : en attente de décision`
}

/**
 * LA CONCORDANCE DE L'ACCUEIL — reddition contre billetterie, avec son SENS.
 *
 * L'ancienne phrase prenait la valeur absolue de l'écart et l'attribuait
 * toujours à « un remboursement après l'émission » : le 26/09/2026, la
 * billetterie DÉPASSAIT la reddition de 1 060 € (les ventes faites le 03/10
 * sur la caisse restée ouverte), ce qu'aucun remboursement n'explique. La
 * billetterie lue ici est celle de la nuit (ventes faites PENDANT elle) :
 *   · reddition > billetterie : un billet remboursé après l'émission, le plus
 *     souvent — ou, si l'écart est exactement celui des ventes de cette
 *     soirée faites pendant une autre nuit, une reddition émise APRÈS elles ;
 *   · billetterie > reddition : des billets vendus après l'émission.
 * `''` quand il n'y a rien à dire.
 */
export function phraseConcordance(c: NightRedditionControlRow | null, avecReddition: boolean, relue: boolean): string {
  if (c && c.ecart_billetterie_cents === 0) return '✓ La reddition et la billetterie de la nuit disent la même chose, au centime.'
  if (c && c.ecart_billetterie_cents !== null) {
    const e = c.ecart_billetterie_cents
    const m = formatMoney(Math.abs(e))
    if (e > 0) {
      const sortant = c.hors_nuit_sortant_cents ?? 0
      return sortant > 0 && sortant === e
        ? `La reddition compte ${m} de plus que les billets vendus pendant la nuit : ce sont les ventes de cette soirée faites pendant une autre nuit, avant son émission : elle les compte, la nuit non.`
        : `La reddition compte ${m} de plus que la billetterie : un billet remboursé après son émission, le plus souvent.`
    }
    return `La billetterie compte ${m} de plus que la reddition : des billets vendus après son émission.`
  }
  if (!avecReddition) return 'Pas encore de reddition Shotgun pour cette nuit : le total vient de la billetterie.'
  if (!relue) return 'La billetterie n’a pas encore été relue après la fin de nuit.'
  return ''
}

/**
 * L'ÉTAT DU PANNEAU, d'un mot : « Décision attendue avant de finaliser » quand
 * un cas bloque ; « En attente » dès qu'un cas attend, même sans argent en jeu
 * (des exonérations : rien ne bloque, mais rien n'est décidé non plus —
 * « Décidé » à côté d'une pastille « En attente » se contredisait) ; « Décidé »
 * seulement quand tout l'est. Un cas sans objet n'attend rien.
 */
export function etatDuPanneau(cases: Array<Pick<OffNightSaleRow, 'status' | 'blocking'>>): { texte: string; classe: string } {
  if (cases.some((c) => c.blocking)) return { texte: 'Décision attendue avant de finaliser', classe: 'lc-pill lc-pill--warning' }
  if (cases.some((c) => c.status === 'EN_ATTENTE')) return { texte: 'En attente', classe: 'lc-pill lc-pill--warning' }
  return { texte: 'Décidé', classe: 'lc-pill lc-pill--ok' }
}

/**
 * CE QUE LE GESTE CHANGERA — ou ce qu'il a changé — à l'Accueil de la nuit
 * lue, en une phrase chiffrée. `null` quand il n'y a rien d'utile à dire (un
 * montant illisible faute de droit, des exonérations seules).
 */
export function effetHorsNuit(c: OffNightSaleRow, chiffres: Pick<ChiffresPointDeVente, 'annonce' | 'constate' | 'complet' | 'source' | 'transfert'> | null): string | null {
  const v = (c.card_present_cents ?? 0) + (c.cash_cents ?? 0)
  if (c.status === 'SANS_OBJET') return null
  if (c.card_present_cents === null || c.cash_cents === null || v === 0) return null
  // Une décision déjà prise, qui ne couvre plus tous les billets (vendus
  // depuis) : sa part est DÉJÀ dans l'attendu. Chiffrer le geste sur le cas
  // entier la compterait une seconde fois.
  if (c.status === 'EN_ATTENTE' && decisionEnVigueur(c) && c.undecided_tickets > 0) {
    const d = c.decision!
    const n = c.undecided_tickets
    return `${billets(d.tickets)} déjà ${d.decision === 'RATTACHER' ? 'rattaché' : 'maintenu'}${d.tickets > 1 ? 's' : ''}, compté${d.tickets > 1 ? 's' : ''} dans l’attendu de cet Accueil ; ${n > 1 ? `les ${n} billets vendus depuis attendent la leur` : 'le billet vendu depuis attend la sienne'}.`
  }
  const annonce = chiffres?.annonce ?? null
  const constate = chiffres?.complet ? chiffres.constate : null
  if (c.side === 'VENTE') {
    if (c.status === 'EN_ATTENTE' && annonce !== null) {
      const apres = annonce + v
      const ecarts = constate === null ? ''
        : `, écart ${formatEcart(constate - apres).texte} au lieu de ${formatEcart(constate - annonce).texte}`
      return `Rattachés à cette nuit : attendu de l’Accueil ${formatMoney(annonce)} + ${formatMoney(v)} = ${formatMoney(apres)}${ecarts}.`
    }
    if (c.status === 'RATTACHE' && annonce !== null && chiffres?.source != null) {
      return `Rattachés : l’attendu de l’Accueil est ${formatMoney(chiffres.source)} du guichet + ${formatMoney(chiffres.transfert)} rattachés = ${formatMoney(annonce)}.`
    }
    if (c.status === 'MAINTENU') return `Maintenus sur la nuit du ${jourMois(c.from_business_date)} : ils ne comptent pas dans l’attendu de cet Accueil, alors que l’argent est dans sa caisse.`
    return null
  }
  // Vu de la nuit de la soirée : ce que la source posée en CONTIENT, en
  // centimes — une source posée « par soirée » avant une revente de la même
  // caisse n'en contient qu'une part, et le geste ne retire que cette part.
  if (c.status === 'EN_ATTENTE') {
    // Un serveur d'avant le montant ne disait que oui / non : oui valait « tout ».
    const dedans = c.source_contains_cents ?? (c.source_contains === true ? v : c.source_contains === false ? 0 : null)
    if (dedans !== null && dedans > 0 && annonce !== null) {
      const part = dedans >= v ? `les contient (${formatMoney(annonce)})` : `en contient ${formatMoney(dedans)} (vendus avant sa pose)`
      return `La source de cet Accueil ${part}. Rattachés à la nuit du ${jourMois(c.sale_business_date)}, l’attendu d’ici redescend à ${formatMoney(annonce - dedans)}${dedans < v ? ` ; maintenus, il monte de ${formatMoney(v - dedans)}` : ''}.`
    }
    if (dedans === 0) {
      return `La source de cet Accueil ne les contient pas : rattachés à la nuit du ${jourMois(c.sale_business_date)}, rien ne change ici ; maintenus, ils s’ajoutent à son attendu.`
    }
    return null
  }
  const inclus = c.decision?.included_cents ?? (c.decision?.from_source_included ? null : 0)
  if (c.status === 'RATTACHE') {
    if (inclus === 0) return `Rattachés à la nuit du ${jourMois(c.sale_business_date)} : ils n’étaient pas dans la source de cet Accueil, rien ne change ici.`
    return inclus === null || inclus >= v
      ? `Rattachés à la nuit du ${jourMois(c.sale_business_date)} : ils sortent de l’attendu de cet Accueil (${formatMoney(v)} de moins que la source).`
      : `Rattachés à la nuit du ${jourMois(c.sale_business_date)} : la part que la source contenait sort de l’attendu de cet Accueil (${formatMoney(inclus)} de moins que la source).`
  }
  if (inclus === null || inclus >= v) return 'Maintenus ici : la source de cet Accueil les contient déjà.'
  return `Maintenus ici : ils s’ajoutent à l’attendu de cet Accueil (${formatMoney(v - inclus)}).`
}


/**
 * LA DÉCISION D'UN CAS S'APPLIQUE-T-ELLE ? La règle vit avec la feuille de
 * caisse, dans le cœur (`feuille-de-caisse.ts`, 15/10/2026) : la feuille et ce
 * panneau doivent dire la même chose, et la feuille n'importe plus rien de
 * l'écran.
 */
export { decisionEnVigueur }

/** Une ligne de l'étape Nuit pour un cas de ventes hors nuit (la table « ce que la nuit annonce »). */
export interface LigneHorsNuitEtape {
  cle: string
  detail: string
  /** Vendus ici, rattachés ici : la ligne mène à l'étape de l'Accueil. */
  accueil: boolean
  carte: number | null
  especes: number | null
  /** Ce que la ligne ajoute à l'annoncé ; `null` = hors de l'annoncé, ou illisible faute de droit. */
  annonce: number | null
  avertissement: string | null
}

/**
 * LES LIGNES DES VENTES HORS NUIT DE L'ÉTAPE NUIT — l'annoncé de l'Accueil y
 * est l'ATTENDU, celui que la finalisation retiendrait : la source posée (ou
 * les ventes de la nuit), plus ce que chaque décision EN VIGUEUR y fait. Ce
 * montant est celui du serveur (`expected_effect_cents`, ses transferts) :
 * l'écran ne le recalcule pas sur la part vivante d'un cas.
 *
 *   · la soirée d'ici vendue pendant une autre nuit, DÉCIDÉE : une ligne de ce
 *     que la décision fait à l'attendu (rattachés, la part que la source
 *     contenait en sort ; maintenus, la part qu'elle ne contenait pas y entre) ;
 *   · les ventes faites ICI sur la soirée d'une autre nuit : une ligne chacune,
 *     dans l'annoncé une fois RATTACHÉES — et ce qui a été vendu depuis la
 *     décision est dit à part, hors de l'annoncé tant qu'il attend la sienne.
 */
export function lignesHorsNuitDeLEtape(cas: OffNightSaleRow[]): LigneHorsNuitEtape[] {
  const out: LigneHorsNuitEtape[] = []
  const effet = (c: OffNightSaleRow): number | null => c.expected_effect_cents ?? null
  for (const c of cas.filter((x) => x.side === 'SOIREE' && decisionEnVigueur(x))) {
    const e = effet(c)
    if (e === 0) continue
    const rattaches = c.decision!.decision === 'RATTACHER'
    out.push({
      cle: `hors-nuit-${c.key}`,
      detail: rattaches
        ? `vendus pendant la nuit du ${jourMois(c.sale_business_date)}, rattachés à cette nuit-là : la part de la source qui les contenait en sort`
        : `vendus pendant la nuit du ${jourMois(c.sale_business_date)}, maintenus ici : la part absente de la source s’ajoute`,
      accueil: false, carte: null, especes: null, annonce: e, avertissement: null,
    })
  }
  for (const c of cas.filter((x) => x.side === 'VENTE' && (x.total_cents ?? 0) > 0)) {
    const enVigueur = decisionEnVigueur(c)
    const rattache = enVigueur && c.decision!.decision === 'RATTACHER'
    const reste = enVigueur ? c.undecided_tickets : 0
    const depuis = reste > 0
      ? `${billets(reste)} vendu${reste > 1 ? 's' : ''} depuis la décision attend${reste > 1 ? 'ent' : ''} la ${reste > 1 ? 'leur' : 'sienne'}, à l’étape Accueil : hors de l’annoncé tant qu’elle n’est pas prise.`
      : null
    out.push({
      cle: `hors-nuit-${c.key}`,
      detail: `vendus cette nuit sur la soirée du ${jourMois(c.from_business_date)}`,
      accueil: rattache,
      // Le TPE et les espèces vendus À CE JOUR : ceux de la décision seulement quand elle les couvre tous.
      carte: reste > 0 ? null : c.card_present_cents,
      especes: reste > 0 ? null : c.cash_cents,
      // Rattachés : ce que le serveur ajoute à l'attendu, pour les billets de la décision.
      annonce: rattache ? effet(c) : null,
      avertissement: rattache ? depuis
        : enVigueur ? [`Maintenus sur la nuit du ${jourMois(c.from_business_date)} : hors de l’annoncé de cette nuit.`, depuis].filter((x): x is string => x !== null).join(' ')
          : c.status === 'SANS_OBJET' ? 'Plus hors de leur nuit : la décision ne s’applique plus.'
            : 'En attente de décision à l’étape Accueil : hors de l’annoncé tant qu’elle n’est pas prise.',
    })
  }
  return out
}
